// Project service for project management operations
// This handles project CRUD operations with Supabase

import { createClerkSupabaseClient } from '@/lib/supabase'
import { logAuthDebug, validateOrgClaimsOrThrow } from '@/lib/debugAuth'
import type { Project } from '@/interfaces'
import type { Database } from '@/interfaces/database.types'
import type { ServiceAuthProps } from '@/interfaces/serviceAuth'

type ProjectInsert = Database['public']['Tables']['projects']['Insert']
type ProjectRow = Database['public']['Tables']['projects']['Row']

/**
 * Create a new project using stable session props
 */
export const createProject = async (
  projectData: Omit<ProjectInsert, 'id' | 'created_at' | 'updated_at'>,
  sessionProps: ServiceAuthProps
): Promise<Project> => {
  const supabase = createClerkSupabaseClient(sessionProps)
  await validateOrgClaimsOrThrow('Projects:createProject', sessionProps)
  await logAuthDebug('Projects:createProject:pre-insert', sessionProps)
  console.debug('Projects:createProject:payload', {
    organization_id: projectData.organization_id,
    user_id: projectData.user_id,
    has_description: !!projectData.description,
  })

  const { data, error } = await supabase.from('projects').insert(projectData).select().single()

  if (error) {
    console.error('Projects:createProject:error', {
      message: error.message,
      code: (error as any).code,
      details: (error as any).details,
      hint: (error as any).hint,
    })
    throw new Error(`Failed to create project: ${error.message || 'Unknown error'}`)
  }

  return mapProjectRowToProject(data)
}

/**
 * Get all projects for the current user's organization using stable session props
 */
export const getProjects = async (sessionProps: ServiceAuthProps): Promise<Project[]> => {
  const supabase = createClerkSupabaseClient(sessionProps)
  await validateOrgClaimsOrThrow('Projects:getProjects', sessionProps)
  await logAuthDebug('Projects:getProjects:pre-query', sessionProps)

  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('updated_at', { ascending: false })

  if (error) {
    // Surface full PostgrestError context for easier diagnosis
    console.error('Projects:getProjects:error', {
      message: error.message,
      code: (error as any).code,
      details: (error as any).details,
      hint: (error as any).hint,
    })
    await logAuthDebug('Projects:getProjects:error-auth', sessionProps)
    throw new Error(`Failed to fetch projects: ${error.message}`)
  }
  if (!data || data.length === 0) {
    console.warn('Projects:getProjects:empty', {
      note: 'Query returned 0 rows. Possible causes: RLS mismatch (JWT org_id/org_slug), empty table, or wrong project scope.',
    })
    await logAuthDebug('Projects:getProjects:empty-auth', sessionProps)
  } else {
    console.debug(`Projects:getProjects:success count=${data.length}`)
  }

  return (data || []).map(mapProjectRowToProject)
}

/**
 * Get a specific project by ID using stable session props
 */
export const getProject = async (id: string, sessionProps: ServiceAuthProps): Promise<Project> => {
  const supabase = createClerkSupabaseClient(sessionProps)
  await validateOrgClaimsOrThrow('Projects:getProject', sessionProps)
  await logAuthDebug('Projects:getProject:pre-query', sessionProps)

  const { data, error } = await supabase.from('projects').select('*').eq('id', id).single()

  if (error) {
    console.error('Projects:getProject:error', {
      message: error.message,
      code: (error as any).code,
      details: (error as any).details,
      hint: (error as any).hint,
    })
    throw new Error(`Failed to fetch project: ${error.message}`)
  }

  return mapProjectRowToProject(data)
}

/**
 * Update a project using stable session props
 */
export const updateProject = async (
  id: string,
  updates: Partial<Omit<ProjectInsert, 'id' | 'created_at' | 'updated_at'>>,
  sessionProps: ServiceAuthProps
): Promise<Project> => {
  const supabase = createClerkSupabaseClient(sessionProps)

  const { data, error } = await supabase
    .from('projects')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update project: ${error.message}`)
  }

  return mapProjectRowToProject(data)
}

/**
 * Delete a project using stable session props
 */
export const deleteProject = async (id: string, sessionProps: ServiceAuthProps): Promise<void> => {
  const supabase = createClerkSupabaseClient(sessionProps)
  await validateOrgClaimsOrThrow('Projects:deleteProject', sessionProps)
  await logAuthDebug('Projects:deleteProject:pre-delete', sessionProps)

  // First check if the project exists and user has access to it
  const { error: fetchError } = await supabase
    .from('projects')
    .select('id, user_id')
    .eq('id', id)
    .single()

  if (fetchError && fetchError.code === 'PGRST116') {
    // Project doesn't exist
    throw new Error(`Project with ID ${id} not found`)
  }

  if (fetchError) {
    console.error('Projects:deleteProject:precheck-error', {
      message: fetchError.message,
      code: (fetchError as any).code,
      details: (fetchError as any).details,
      hint: (fetchError as any).hint,
    })
    throw new Error(`Failed to check project: ${fetchError.message}`)
  }

  // Now attempt to delete with count to verify deletion
  const { error, count } = await supabase.from('projects').delete({ count: 'exact' }).eq('id', id)

  if (error) {
    console.error('Projects:deleteProject:error', {
      message: error.message,
      code: (error as any).code,
      details: (error as any).details,
      hint: (error as any).hint,
    })
    throw new Error(`Failed to delete project: ${error.message}`)
  }

  // If no rows were deleted but project exists, RLS likely blocked it
  if (count === 0) {
    console.warn('Projects:deleteProject:rls-denied', { id })
    throw new Error(`Access denied: You don't have permission to delete this project`)
  }
}

/**
 * Helper function to map database row to Project interface
 */
function mapProjectRowToProject(row: ProjectRow): Project {
  return {
    id: row.id,
    name: row.name,
    description: row.description || undefined,
    userId: row.user_id,
    organizationId: row.organization_id,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
  }
}
